import type {
  NotificationSettingResponse,
  UpdateNotificationSettingDto,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface NotificationSettingState {
  setting: NotificationSettingResponse | null;
  status: StateStatus;
}

interface NotificationSettingActions {
  fetchSetting: (workspaceId: number) => Promise<void>;
  updateSetting: (
    workspaceId: number,
    dto: UpdateNotificationSettingDto,
  ) => Promise<NotificationSettingResponse>;
  reset: () => void;
}

export type NotificationSettingStore = NotificationSettingState &
  NotificationSettingActions;

const initialState: NotificationSettingState = {
  setting: null,
  status: "idle",
};

export function createNotificationSettingStore(deps: StoreDeps) {
  return createStore<NotificationSettingStore>(
    "NotificationSettingStore",
    (set) => ({
      ...initialState,

      fetchSetting: (workspaceId) =>
        withFetchStatus(
          (s) => set((state) => { state.status = s; }),
          deps.logger,
          async () => {
            const setting =
              await deps.api.notificationSetting.get(workspaceId);
            set((state) => { state.setting = setting; });
          },
        ),

      updateSetting: async (workspaceId, dto) => {
        const setting = await deps.api.notificationSetting.update(
          workspaceId,
          dto,
        );
        set((state) => { state.setting = setting; });
        return setting;
      },

      reset: createReset(
        set as ImmerSet<NotificationSettingState>,
        initialState,
      ),
    }),
  );
}
